import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import type { ShopConfig, Hours, Palette } from "../lib/types";
import { defaultHours, sagePalette, DEFAULT_PRESET, DEFAULT_TIMEZONE } from "../lib/constants";

const SHOP_REF = () => doc(db, "config", "shop");
const CLOSED_REF = () => doc(db, "config", "closedDates");

// Used before config loads, and as the shape new shops are seeded with.
export const defaultShopConfig: ShopConfig = {
  shopName: "Radiance",
  logoUrl: "",
  shopEmail: "",
  staffEmails: [],
  timezone: DEFAULT_TIMEZONE,
  adminMessage: "",
  blockSameDay: false,
  cooldownMinutes: 0,
  hours: defaultHours,
  palette: sagePalette,
  palettePreset: DEFAULT_PRESET,
};

// Firestore can return hours with string keys; normalize to a 0..6 map.
function normalizeHours(raw: unknown): Hours {
  if (!raw || typeof raw !== "object") return defaultHours;
  const out: Hours = { ...defaultHours };
  for (let i = 0; i < 7; i++) {
    const v = (raw as Record<string, unknown>)[String(i)] ?? (raw as Record<number, unknown>)[i];
    if (v && typeof v === "object") {
      const d = v as Partial<Hours[number]>;
      out[i] = {
        open: Boolean(d.open),
        start: typeof d.start === "number" ? d.start : defaultHours[i].start,
        end: typeof d.end === "number" ? d.end : defaultHours[i].end,
      };
    }
  }
  return out;
}

function normalizePalette(raw: unknown): Palette {
  if (!raw || typeof raw !== "object") return sagePalette;
  const d = raw as Partial<Palette>;
  return {
    accent: d.accent ?? sagePalette.accent,
    accentDark: d.accentDark ?? sagePalette.accentDark,
    accentSoft: d.accentSoft ?? sagePalette.accentSoft,
    bg: d.bg ?? sagePalette.bg,
    surface: d.surface ?? sagePalette.surface,
    ink: d.ink ?? sagePalette.ink,
    muted: d.muted ?? sagePalette.muted,
    line: d.line ?? sagePalette.line,
  };
}

export function subscribeShopConfig(
  onData: (cfg: ShopConfig) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    SHOP_REF(),
    (snap) => {
      if (!snap.exists()) {
        onData(defaultShopConfig);
        return;
      }
      const raw = snap.data();
      onData({
        shopName: typeof raw.shopName === "string" ? raw.shopName : defaultShopConfig.shopName,
        logoUrl: typeof raw.logoUrl === "string" ? raw.logoUrl : "",
        shopEmail: typeof raw.shopEmail === "string" ? raw.shopEmail : "",
        staffEmails: Array.isArray(raw.staffEmails)
          ? (raw.staffEmails as unknown[]).filter((e): e is string => typeof e === "string" && e.trim() !== "")
          : [],
        timezone: typeof raw.timezone === "string" ? raw.timezone : DEFAULT_TIMEZONE,
        adminMessage: typeof raw.adminMessage === "string" ? raw.adminMessage : "",
        blockSameDay: raw.blockSameDay === true,
        cooldownMinutes: typeof raw.cooldownMinutes === "number" && raw.cooldownMinutes > 0
          ? Math.round(raw.cooldownMinutes) : 0,
        hours: normalizeHours(raw.hours),
        palette: normalizePalette(raw.palette),
        palettePreset: typeof raw.palettePreset === "string" ? raw.palettePreset : DEFAULT_PRESET,
      });
    },
    (err) => onError(err),
  );
}

// Admin-only write. Merges so partial updates are safe.
export async function saveShopConfig(patch: Partial<ShopConfig>): Promise<void> {
  await setDoc(SHOP_REF(), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}

// ---- closed dates (config/closedDates: { dates: string[] }) ----

export function subscribeClosedDates(
  onData: (dates: string[]) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    CLOSED_REF(),
    (snap) => {
      const raw = snap.exists() ? snap.data().dates : [];
      onData(Array.isArray(raw) ? (raw as string[]).slice().sort() : []);
    },
    (err) => onError(err),
  );
}

export async function saveClosedDates(dates: string[]): Promise<void> {
  await setDoc(CLOSED_REF(), { dates: Array.from(new Set(dates)).sort() }, { merge: true });
}
